import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';

/**
 * Gobernanza U7 — Viento Recio por organización (solo super admin vía modales de Organizaciones).
 */
export default function OrgSpiritualConfigPanel({
  entityType,
  entityId,
  isSede = false,
  onSaved,
}) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [inheritMode, setInheritMode] = useState('inherit');

  const basePath = entityType === 'trainer' ? '/trainers' : '/gyms';

  useEffect(() => {
    if (!entityId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`${basePath}/${entityId}`);
        const row = data?.trainer || data?.gym || data;
        if (!active) return;
        if (entityType === 'trainer') {
          setEnabled(row?.spiritual_enabled === true);
        } else if (isSede) {
          const flag = row?.spiritual_enabled;
          if (flag === true) setInheritMode('on');
          else if (flag === false) setInheritMode('off');
          else setInheritMode('inherit');
          setEnabled(flag === true);
        } else {
          setEnabled(row?.spiritual_enabled === true);
        }
      } catch {
        if (active) setEnabled(false);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [basePath, entityId, entityType, isSede]);

  const save = async (nextEnabled, nextInherit = inheritMode) => {
    if (!entityId) return;
    setSaving(true);
    try {
      let payload;
      if (entityType === 'trainer') {
        payload = { spiritual_enabled: nextEnabled === true };
      } else if (isSede) {
        if (nextInherit === 'inherit') payload = { spiritual_enabled: null };
        else if (nextInherit === 'on') payload = { spiritual_enabled: true };
        else payload = { spiritual_enabled: false };
      } else {
        payload = { spiritual_enabled: nextEnabled === true };
      }
      await api.put(`${basePath}/${entityId}`, payload);
      emitToast({
        type: 'success',
        title: t('orgs.spiritual_saved', 'Viento Recio actualizado'),
      });
      onSaved?.();
    } catch (err) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: err.response?.data?.error || t('orgs.spiritual_save_error', 'No se pudo guardar'),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!entityId) return null;

  return (
    <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Sparkles className="w-5 h-5 text-violet-700 shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-violet-950">{t('orgs.spiritual_title', 'Viento Recio')}</h4>
          <p className="text-xs text-violet-900/80 mt-1">
            {t('orgs.spiritual_hint', 'Módulo espiritual no comercial. Solo super administrador. Los usuarios de esta organización heredan acceso automáticamente.')}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-stone-500">{t('common.loading', 'Cargando…')}</p>
      ) : entityType === 'gym' && isSede ? (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-stone-800">
            {t('orgs.spiritual_sede_mode', 'Configuración de sede')}
          </label>
          <select
            className="input max-w-md"
            value={inheritMode}
            disabled={saving}
            onChange={async (e) => {
              const mode = e.target.value;
              setInheritMode(mode);
              setEnabled(mode === 'on');
              await save(mode === 'on', mode);
            }}
          >
            <option value="inherit">{t('orgs.spiritual_inherit', 'Heredar de organización principal')}</option>
            <option value="on">{t('orgs.spiritual_on', 'Habilitado')}</option>
            <option value="off">{t('orgs.spiritual_off', 'Desactivado')}</option>
          </select>
        </div>
      ) : (
        <label className="inline-flex items-center gap-2 text-sm text-stone-800">
          <input
            type="checkbox"
            checked={enabled}
            disabled={saving}
            onChange={async (e) => {
              const next = e.target.checked;
              setEnabled(next);
              await save(next);
            }}
          />
          {t('orgs.spiritual_enabled', 'Habilitado')}
        </label>
      )}
    </div>
  );
}
